import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { Box } from '@material-ui/core'

const typingDot = keyframes`
  0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-2px); }
`

const MessageList = styled('div')({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowAnchor: 'none',
  display: 'flex',
  flexDirection: 'column',
})

const MessageListInner = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '50%',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
  margin: '0 auto',
  padding: theme.spacing(0, 4, 0),
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
}))

const ChatTurn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  '& + &': { marginTop: theme.spacing(3) },
}))

// Its height is set by useChatAnchorScroll.
const BottomGutter = styled('div')({
  pointerEvents: 'none',
  flexShrink: 0,
})

const MessageContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isUser'
})<{ $isUser: boolean }>(({ $isUser }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: $isUser ? 'flex-end' : 'flex-start',
  width: '100%',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  '& pre': {
    whiteSpace: 'pre-wrap',
  },
}))

const MessageBubble = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  maxWidth: '85%',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word'
}))

const MessageBubbleWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  minWidth: 0,
  gap: 6,
})

const MessageTime = styled('time', {
  shouldForwardProp: (prop) => prop !== '$isUser'
})<{ $isUser: boolean }>(({ theme, $isUser }) => ({
  alignSelf: $isUser ? 'flex-end' : 'flex-start',
  marginTop: theme.spacing(0.5),
  fontSize: theme.typography.caption.fontSize,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}))

const TypingIndicator = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  minHeight: theme.spacing(3),
  '& span': {
    width: 5,
    height: 5,
    borderRadius: '50%',
    backgroundColor: theme.palette.text.secondary,
    animation: `${typingDot} 1.2s infinite ease-in-out`,
  },
  '& span:nth-of-type(2)': {
    animationDelay: '0.15s',
  },
  '& span:nth-of-type(3)': {
    animationDelay: '0.3s',
  },
}))

export {
  MessageList,
  MessageListInner,
  ChatTurn,
  BottomGutter,
  MessageContainer,
  MessageBubble,
  MessageBubbleWrapper,
  MessageTime,
  TypingIndicator,
}
